import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { MAX_COMMENTS_PER_CALL, type QAComment, type QAEntityType } from '@intone/shared';

import { useQAComments, useAddQAComment, useUpdateQAComment } from '../model/useQAComments';

interface QACommentSectionParams {
  entityId: string;
  entityType: QAEntityType;
  userId: string;
  authorName?: string;
}

export function useQACommentSection({ entityId, entityType, userId, authorName }: QACommentSectionParams) {
  const [editingId, setEditingId] = useState<number | null>(null);

  const { data: comments = [], isLoading: commentsLoading } = useQAComments(entityId, entityType);

  const addRef = useRef<HTMLTextAreaElement>(null);
  const editRef = useRef<HTMLTextAreaElement>(null);

  const [hasComment, setHasComment] = useState(false);

  // Reset when entity changes
  useEffect(() => {
    setEditingId(null);
    setHasComment(false);
    if (addRef.current) addRef.current.value = '';
  }, [entityId])

  const addMutation = useAddQAComment();

  const updateMutation = useUpdateQAComment();

  const isAddCommentPending = addMutation.isPending;
  const isCommentPending = commentsLoading || addMutation.isPending || updateMutation.isPending;

  const canAddComment = comments.length <  MAX_COMMENTS_PER_CALL;

  const handleAddComment = async () => {
    const comment = addRef.current?.value.trim() ?? ''
    if (!comment || !canAddComment) {
      toast.warning("Comment can't be empty");
      return;
    }

    addMutation.mutate(
      {
        id: 1,
        entity_id: entityId,
        entity_type: entityType,
        author_id: userId || 'Unknown',
        author_name: authorName || 'Unknown',
        message: comment,
        created_at: new Date(),
        updated_at: new Date(),
      },
      {
        onSuccess: (response) => {
          if (addRef.current) addRef.current.value = '';
          setHasComment(false);
          toast.success(response.message, { id: 'qa-comment-add' });
        },
        onError: (err: Error) => {
          toast.error(err.message || 'Failed to add comment', { id: 'qa-comment-add-failed' });
        },
      }
    );
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    if (editRef.current) editRef.current.value = '';
  };

  const handleStartEdit = (comment: QAComment) => {
    setEditingId(comment.id);
    // Fill text after render
    setTimeout(() => {
      if (editRef.current) {
        editRef.current.value = comment.message;
      }
    }, 0)
  };

  const handleUpdateComment = (e: React.FormEvent<HTMLFormElement>, comment: QAComment) => {
    e.preventDefault();
    if (!editRef.current) return;
    const value = editRef.current.value.trim();
    if (!value) return;
    updateMutation.mutate(
      {
        ...comment,
        message: value,
        updated_at: new Date(),
      },
      {
        onSuccess: (response) => {
          setEditingId(null);
          if (editRef.current) editRef.current.value = '';
          toast.success(response.message, { id: 'comment-update' });
        },
        onError: (err: Error) => {
          toast.error(err.message || 'Failed to update comment', { id: 'comment-update-failed' });
        },
      },
    );
  };

  return {
    addRef,
    canAddComment,
    comments,
    editingId,
    editRef,
    hasComment,
    isAddCommentPending,
    isCommentPending,

    setHasComment,
    handleAddComment,
    handleCancelEdit,
    handleStartEdit,
    handleUpdateComment,
  };
}
